import { Book, Video, ExternalLink, FileText, Headphones } from 'lucide-react';
import resourcesBackground from '../assets/hero-background.jpg';

export default function Resources() {
  const articles = [
    {
      title: 'The Role of Nuclear Energy in Clean Energy Transitions',
      source: 'International Energy Agency',
      url: 'https://www.iea.org/reports/nuclear-power-in-a-clean-energy-system',
      description: 'Comprehensive analysis of nuclear energy\'s role in achieving global climate goals.'
    },
    {
      title: 'How Does a Nuclear Reactor Work?',
      source: 'World Nuclear Association',
      url: 'https://world-nuclear.org/nuclear-essentials/how-does-a-nuclear-reactor-work',
      description: 'An accessible explanation of nuclear reactor technology and the fuel cycle.'
    },
    {
      title: 'The Future of Nuclear Power',
      source: 'MIT Energy Initiative',
      url: 'https://energy.mit.edu/research/future-nuclear-power/',
      description: 'Research on nuclear energy\'s critical role in decarbonization efforts.'
    },
    {
      title: 'Small Modular Reactors (SMRs)',
      source: 'U.S. Department of Energy',
      url: 'https://www.energy.gov/ne/advanced-small-modular-reactors-smrs',
      description: 'Overview of next-generation small modular reactor technology.'
    }
  ];

  const videos = [
    {
      title: 'Nuclear Energy Explained',
      source: 'Kurzgesagt',
      url: 'https://www.youtube.com/watch?v=rcOFV4y5z8c',
      description: 'Engaging animated explanation of nuclear power and its benefits.'
    },
    {
      title: 'Why I Changed My Mind About Nuclear Power',
      source: 'Sabine Hossenfelder',
      url: 'https://www.youtube.com/watch?v=0kahih8RT1k',
      description: 'A physicist\'s perspective on nuclear energy\'s role in climate solutions.'
    },
    {
      title: 'The Economics of Nuclear Energy',
      source: 'Real Engineering',
      url: 'https://www.youtube.com/watch?v=UC_BCz0pzMw',
      description: 'Deep dive into the costs and economic considerations of nuclear power.'
    }
  ];

  const books = [
    {
      title: 'A Bright Future: How Some Countries Have Solved Climate Change',
      author: 'Joshua Goldstein & Staffan Qvist',
      description: 'Explores how nuclear energy has successfully reduced emissions in several countries.'
    },
    {
      title: 'The Making of the Atomic Bomb',
      author: 'Richard Rhodes',
      description: 'Pulitzer Prize-winning history of nuclear technology development.'
    },
    {
      title: 'Power to Save the World: The Truth About Nuclear Energy',
      author: 'Gwyneth Cravens',
      description: 'A journalist\'s journey from nuclear skeptic to advocate.'
    }
  ];

  const podcasts = [
    {
      title: 'Titans of Nuclear',
      description: 'Interviews with leaders in the nuclear energy industry.',
      url: 'https://www.titansofnuclear.com/'
    },
    {
      title: 'Decouple Podcast',
      description: 'Discussions on energy, climate, and the role of nuclear power.',
      url: 'https://www.decouple.media/'
    }
  ];

  const faqs = [
    {
      question: 'Is nuclear energy safe?',
      answer: 'Yes. Nuclear energy is one of the safest forms of energy production. Modern reactors have multiple safety systems, and the industry has an excellent safety record. Deaths per unit of energy produced are lower for nuclear than for fossil fuels.'
    },
    {
      question: 'What about nuclear waste?',
      answer: 'Nuclear waste is carefully managed and stored. The volume is remarkably small compared to other energy sources. Advanced reactors and recycling technologies can further reduce waste. Long-term storage solutions like deep geological repositories are being developed.'
    },
    {
      question: 'How does nuclear energy help fight climate change?',
      answer: 'Nuclear power produces no greenhouse gas emissions during operation. It provides reliable, baseload electricity 24/7, complementing intermittent renewables like solar and wind. It\'s essential for achieving deep decarbonization.'
    },
    {
      question: 'Is nuclear energy expensive?',
      answer: 'Initial construction costs are high, but nuclear plants operate for 60+ years with low fuel costs. Lifetime costs are competitive with other clean energy sources. New small modular reactors aim to reduce upfront costs significantly.'
    },
    {
      question: 'Can nuclear energy coexist with renewable energy?',
      answer: 'Absolutely! Nuclear and renewables complement each other. Nuclear provides steady baseload power while renewables provide variable generation. Together, they create a robust, carbon-free electricity system.'
    }
  ];

  return (
    <section className="pt-24 py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
        style={{ backgroundImage: `url(${resourcesBackground})` }}
      />
      <div className="absolute inset-0 bg-gray-50 opacity-50" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12" data-aos="fade-down">
          <Book className="w-12 h-12 text-michigan-blue mx-auto mb-4" />
          <h2 className="text-4xl font-bold text-michigan-blue mb-4">Resources & Learning</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore curated resources to deepen your understanding of nuclear energy, from beginner-friendly content to advanced research.
          </p>
        </div>

        <div className="mb-16" data-aos="fade-up">
          <div className="flex items-center mb-6">
            <FileText className="w-8 h-8 text-michigan-blue mr-3" />
            <h3 className="text-3xl font-bold text-michigan-blue">Articles & Reports</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((article, idx) => (
              <a key={idx} href={article.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition border-l-4 border-michigan-maize group" data-aos="fade-up" data-aos-delay={idx * 100}>
                <div className="flex items-start justify-between">
                  <div className="flex-grow">
                    <h4 className="text-xl font-semibold text-michigan-blue group-hover:text-blue-700 mb-2">{article.title}</h4>
                    <p className="text-sm text-michigan-maize font-medium mb-2">{article.source}</p>
                    <p className="text-gray-600 text-sm">{article.description}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-michigan-blue transition ml-4 flex-shrink-0" />
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="mb-16" data-aos="fade-up">
          <div className="flex items-center mb-6">
            <Video className="w-8 h-8 text-michigan-blue mr-3" />
            <h3 className="text-3xl font-bold text-michigan-blue">Educational Videos</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {videos.map((video, idx) => (
              <a key={idx} href={video.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition border-t-4 border-michigan-blue group" data-aos="fade-up" data-aos-delay={idx * 100}>
                <div className="flex items-start justify-between mb-3">
                  <Video className="w-10 h-10 text-michigan-maize" />
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-michigan-blue transition" />
                </div>
                <h4 className="text-lg font-semibold text-michigan-blue group-hover:text-blue-700 mb-2">{video.title}</h4>
                <p className="text-sm text-michigan-maize font-medium mb-2">{video.source}</p>
                <p className="text-gray-600 text-sm">{video.description}</p>
              </a>
            ))}
          </div>
        </div>

        <div className="mb-16" data-aos="fade-up">
          <div className="flex items-center mb-6">
            <Book className="w-8 h-8 text-michigan-blue mr-3" />
            <h3 className="text-3xl font-bold text-michigan-blue">Recommended Books</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {books.map((book, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-michigan-maize" data-aos="fade-up" data-aos-delay={idx * 100}>
                <Book className="w-10 h-10 text-michigan-blue mb-3" />
                <h4 className="text-lg font-semibold text-michigan-blue mb-2">{book.title}</h4>
                <p className="text-sm text-michigan-maize font-medium mb-2">by {book.author}</p>
                <p className="text-gray-600 text-sm">{book.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16" data-aos="fade-up">
          <div className="flex items-center mb-6">
            <Headphones className="w-8 h-8 text-michigan-blue mr-3" />
            <h3 className="text-3xl font-bold text-michigan-blue">Podcasts</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {podcasts.map((podcast, idx) => (
              <a key={idx} href={podcast.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition border-l-4 border-michigan-blue group" data-aos="fade-up" data-aos-delay={idx * 100}>
                <div className="flex items-start justify-between">
                  <div className="flex-grow">
                    <div className="flex items-center mb-2">
                      <Headphones className="w-6 h-6 text-michigan-maize mr-2" />
                      <h4 className="text-xl font-semibold text-michigan-blue group-hover:text-blue-700">{podcast.title}</h4>
                    </div>
                    <p className="text-gray-600">{podcast.description}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-michigan-blue transition ml-4 flex-shrink-0" />
                </div>
              </a>
            ))}
          </div>
        </div>

        <div data-aos="fade-up">
          <h3 className="text-3xl font-bold text-michigan-blue mb-6 text-center">Frequently Asked Questions</h3>
          <div className="space-y-4 max-w-4xl mx-auto">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-michigan-maize" data-aos="fade-up" data-aos-delay={idx * 50}>
                <h4 className="text-xl font-semibold text-michigan-blue mb-3">{faq.question}</h4>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}